import { HttpClient, HttpEventType, HttpParams } from '@angular/common/http';
import { inject, Injectable, resource } from '@angular/core';
import { lastValueFrom, map } from 'rxjs';
import {
  BuyerAccount,
  BuyerAccountResponse,
  BuyerAccountUpdateResponse,
  SyncResponse,
} from '../../models/buyer-account.model';
import { CommonService } from '../common/common.service';
import {
  mapBuyerAccountResponseToModel,
  mapBuyerAccountToParams,
} from './buyer-account.mapper';

@Injectable({
  providedIn: 'root',
})
export class BuyerAccountService {
  http = inject(HttpClient);
  common = inject(CommonService);

  buyersRaw$ = this.http
    .get<BuyerAccountResponse[]>(`BuyerLogin`)
    .pipe(
      map((response: BuyerAccountResponse[]) =>
        response.map((r) => mapBuyerAccountResponseToModel(r)),
      ),
    );

  activeBuyers$ = this.buyersRaw$.pipe(
    map((buyers: BuyerAccount[]) => buyers.filter((b) => b.activeFlag)),
  );

  inactiveBuyers$ = this.buyersRaw$.pipe(
    map((buyers: BuyerAccount[]) => buyers.filter((b) => !b.activeFlag)),
  );

  buyers = resource({
    loader: (): Promise<BuyerAccount[]> => lastValueFrom(this.activeBuyers$),
  });

  inactiveBuyers = resource({
    loader: (): Promise<BuyerAccount[]> => lastValueFrom(this.inactiveBuyers$),
  });

  deleteBuyerLogin(buyer: BuyerAccount) {
    const updatedBuyer = {
      ...buyer,
      activeFlag: false,
      updatedOn: new Date(),
    };

    return this.http.put<BuyerAccountUpdateResponse>(
      'BuyerLogin',
      mapBuyerAccountToParams(updatedBuyer),
    );
  }

  saveBuyer(buyer: BuyerAccount) {
    const updatedBuyer = {
      ...buyer,
      activeFlag: true,
      lastLoginDate: new Date(),
      updatedOn: new Date(),
    };

    return this.http.put<BuyerAccountUpdateResponse>(
      `BuyerLogin`,
      mapBuyerAccountToParams(updatedBuyer),
    );
  }

  createBuyer(account: BuyerAccount) {
    return this.http.post<BuyerAccountUpdateResponse>(
      `BuyerLogin`,
      mapBuyerAccountToParams(account),
    );
  }

  uploadRedFlagBuyers = (file: File) => {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http
      .post('BuyerLogin/RedflagBuyers', formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            return Math.round((100 * event.loaded) / (event.total || 1));
          } else {
            return 0;
          }
        }),
      );
  };

  syncBuyerLogin(wmsCode: string | null | undefined) {
    const params = new HttpParams().set('wmsCode', wmsCode || '');

    return this.http.post<SyncResponse>('BuyerLogin/Sync', {}, { params });
  }
}
